import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportCheckStatusBatchProcessedRows,
  getCheckStatusBatchProcessedRows,
  getCheckStatusBatchUploadHistory,
} from "../../services/settlementApi";

const CheckStatusBatchUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();
  const [uploadInfo, setUploadInfo] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  const [filterCvNo, setFilterCvNo] = useState("");
  const [filterCheckNo, setFilterCheckNo] = useState("");
  const [filterCheckStatus, setFilterCheckStatus] = useState("");
  const [filterReplacement, setFilterReplacement] = useState("");
  const [filterResult, setFilterResult] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  useEffect(() => {
    const fetchUploadInfo = async () => {
      try {
        const response = await getCheckStatusBatchUploadHistory({ page: 1, limit: 1000 });
        const upload = response.data.data?.find((item) => item.upload_id === uploadId);
        if (upload) {
          setUploadInfo(upload);
        } else {
          toast.error("Upload not found.");
          navigate("/check-status-batch-upload-history");
        }
      } catch (error) {
        toast.error("Failed to fetch upload information.");
        console.error(error);
      }
    };

    if (uploadId) fetchUploadInfo();
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;

    setLoading(true);
    try {
      const response = await getCheckStatusBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      setAllRows(response.data.data || []);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const formatDate = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return "-";
    const iso = text.slice(0, 10);
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return text;
    return `${match[2]}/${match[3]}/${match[1]}`;
  };

  const filteredRows = useMemo(() => {
    const match = (value, filter) => !filter || String(value ?? "").toLowerCase().includes(filter.toLowerCase());

    return allRows.filter((row) => {
      if (!match(row.cv_no, filterCvNo)) return false;
      if (!match(row.check_no, filterCheckNo)) return false;
      if (!match(row.check_status, filterCheckStatus)) return false;
      if (filterReplacement !== "" && String(row.replacement_check_indicator ?? "") !== filterReplacement) return false;
      if (filterResult !== "" && row.result_type !== filterResult) return false;

      const details = [
        row.error_message,
        row.processed_by,
        row.reason_for_replacement,
        row.remarks,
        row.original_check_number,
        row.new_check_number,
      ].join(" ");
      if (!match(details, filterDetails)) return false;

      return true;
    });
  }, [allRows, filterCvNo, filterCheckNo, filterCheckStatus, filterReplacement, filterResult, filterDetails]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const hasActiveFilters = filterCvNo || filterCheckNo || filterCheckStatus || filterReplacement || filterResult || filterDetails;

  const clearFilters = () => {
    setFilterCvNo("");
    setFilterCheckNo("");
    setFilterCheckStatus("");
    setFilterReplacement("");
    setFilterResult("");
    setFilterDetails("");
    setPage(1);
  };

  const handleExport = async (type) => {
    if (!uploadId) return;

    setExporting(true);
    try {
      const response = await exportCheckStatusBatchProcessedRows(uploadId, { type });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `check_status_batch_${uploadId}_${type}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type} rows.`);
    } catch (error) {
      toast.error("Export failed.");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const resultBadge = (resultType) => {
    if (resultType === "success") {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>;
    }
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>;
  };

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <p className="text-center py-12 text-gray-600">Loading upload information...</p>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/check-status-batch-upload-history")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Check Status Batch Processed</h1>
            <p className="text-gray-600 text-sm">{uploadInfo.original_file_name}</p>
          </div>
        </div>
        <button
          onClick={fetchRows}
          disabled={loading}
          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-5 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Rows</p>
            <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Success</p>
            <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <p className={`text-lg font-bold ${uploadInfo.status === "completed" ? "text-green-600" : uploadInfo.status === "failed" ? "text-red-600" : "text-yellow-600"}`}>
              {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
              <FontAwesomeIcon icon={faUser} className="text-gray-400" />
              {uploadInfo.uploaded_by_name || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Upload ID</p>
            <p className="text-xs font-mono text-gray-700 break-all">{uploadInfo.upload_id}</p>
          </div>
        </div>
        {uploadInfo.error_message && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800"><strong>Error:</strong> {uploadInfo.error_message}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "all", label: `All Rows (${uploadInfo.total_rows || 0})`, active: "bg-blue-600 text-white" },
              { key: "success", label: `Success (${uploadInfo.success_count || 0})`, active: "bg-green-600 text-white" },
              { key: "failed", label: `Failed (${uploadInfo.failed_count || 0})`, active: "bg-red-600 text-white" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  setRowType(item.key);
                  setPage(1);
                }}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  rowType === item.key ? item.active : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleExport("all")}
              disabled={exporting || (uploadInfo.total_rows || 0) === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export All
            </button>
            <button
              onClick={() => handleExport("success")}
              disabled={exporting || (uploadInfo.success_count || 0) === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export Success
            </button>
            <button
              onClick={() => handleExport("failed")}
              disabled={exporting || (uploadInfo.failed_count || 0) === 0}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export Failed
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
            Filter Rows
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1">
              <FontAwesomeIcon icon={faTimes} />
              Clear Filters
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <input
            type="text"
            value={filterCvNo}
            onChange={(e) => {
              setFilterCvNo(e.target.value);
              setPage(1);
            }}
            placeholder="CV No."
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            value={filterCheckNo}
            onChange={(e) => {
              setFilterCheckNo(e.target.value);
              setPage(1);
            }}
            placeholder="Check No."
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          />
          <select
            value={filterCheckStatus}
            onChange={(e) => {
              setFilterCheckStatus(e.target.value);
              setPage(1);
            }}
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          >
            <option value="">All Check Status</option>
            <option value="For Clearing">For Clearing</option>
            <option value="Cleared">Cleared</option>
            <option value="Outstanding">Outstanding</option>
            <option value="Stale">Stale</option>
            <option value="Replaced">Replaced</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <select
            value={filterReplacement}
            onChange={(e) => {
              setFilterReplacement(e.target.value);
              setPage(1);
            }}
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          >
            <option value="">All Replacement</option>
            <option value="1">Replacement</option>
            <option value="0">Non-replacement</option>
          </select>
          <select
            value={filterResult}
            onChange={(e) => {
              setFilterResult(e.target.value);
              setPage(1);
            }}
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          >
            <option value="">All Result</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <input
            type="text"
            value={filterDetails}
            onChange={(e) => {
              setFilterDetails(e.target.value);
              setPage(1);
            }}
            placeholder="Reason, remarks, error..."
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs lg:text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Row</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Result</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">CV No.</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Check No.</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Check Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Stale Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Replacement</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Reason / Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-3 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-3 py-8 text-center text-gray-500">No rows found</td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => (
                  <tr key={`${row.result_type}-${row.id}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                    <td className="px-3 py-2.5">{resultBadge(row.result_type)}</td>
                    <td className="px-3 py-2.5 text-gray-800">{row.cv_no || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-800">{row.check_no || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-800">{row.check_status || "-"}</td>
                    <td className="px-3 py-2.5 text-gray-700">{formatDate(row.check_date)}</td>
                    <td className="px-3 py-2.5 text-gray-700">{formatDate(row.computed_stale_date)}</td>
                    <td className="px-3 py-2.5 text-gray-700">
                      {Number(row.replacement_check_indicator || 0) === 1 ? (
                        <div>
                          <p className="font-semibold text-purple-700">Yes</p>
                          <p className="text-[11px] text-gray-500">{row.original_check_number || "-"} to {row.new_check_number || "-"}</p>
                        </div>
                      ) : (
                        "No"
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs max-w-sm">
                      {row.error_message ? (
                        <div className="text-red-700" title={row.error_message}>{row.error_message}</div>
                      ) : (
                        <div className="text-gray-700">
                          <p>{row.reason_for_replacement || row.remarks || "Processed"}</p>
                          <p className="text-gray-500">By {row.processed_by || "-"}</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <span className="text-sm font-medium text-gray-700">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CheckStatusBatchUploadProcessedPage;
